'use client';

import React from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { ArrowLeft, Calendar, Clock, User, Tag, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';
import { getArticleBySlug, getRelatedArticles } from '@/lib/blog';
import type { BlogArticle, BlogSection } from '@/lib/blog';

// Article paragraphs (which come from translator-editable JSON) support exactly one
// piece of inline markup: an internal link written as [anchor text](/path).
// Nothing else is parsed and no HTML is ever injected — we build React nodes only.
// Safe to hoist despite the /g flag: String.split never reads or advances lastIndex.
const INLINE_LINK_PATTERN = /\[([^\]]+)\]\((\/[^)]*)\)/g;

// Only root-relative paths are linkable. Absolute ("https://..."), protocol-relative
// ("//evil.com") and scheme URLs ("javascript:...") are rejected so a bad or tampered
// translation string can never redirect readers off-site or run a script. Browsers
// strip tab/CR/LF from URLs, so "/\n/evil.com" would resolve as "//evil.com" — the
// path is normalised before the check to close that bypass.
const isSafeInternalPath = (path: string) => /^\/(?![/\\])/.test(path.replace(/[\t\r\n]/g, ''));

// Splitting on a 2-capture-group regex yields [text, anchor, path, text, anchor, path, ..., text].
const renderParagraph = (text: string, keyPrefix: string): React.ReactNode => {
  const parts = text.split(INLINE_LINK_PATTERN);

  // No link syntax present: render the untouched string, exactly as before.
  if (parts.length === 1) return text;

  const nodes: React.ReactNode[] = [];

  for (let i = 0; i < parts.length; i += 3) {
    const plain = parts[i];
    if (plain) {
      nodes.push(<React.Fragment key={`${keyPrefix}-t${i}`}>{plain}</React.Fragment>);
    }

    const anchor = parts[i + 1];
    const path = parts[i + 2];
    if (anchor === undefined || path === undefined) continue;

    if (isSafeInternalPath(path)) {
      nodes.push(
        <Link
          key={`${keyPrefix}-l${i}`}
          href={path}
          className="text-primary-500 underline underline-offset-2 hover:text-primary-600 transition-colors"
        >
          {anchor}
        </Link>
      );
    } else {
      // Rejected target: keep the author's literal text rather than silently dropping it.
      nodes.push(
        <React.Fragment key={`${keyPrefix}-r${i}`}>{`[${anchor}](${path})`}</React.Fragment>
      );
    }
  }

  return nodes;
};

export default function ArticleContent() {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const slug = typeof params.slug === 'string' ? params.slug : '';

  const article = getArticleBySlug(slug);
  const relatedArticles = getRelatedArticles(slug, 3);

  const { ref: headerRef, isVisible: headerVisible } = useIntersectionObserver();
  const { ref: bodyRef, isVisible: bodyVisible } = useIntersectionObserver();
  const { ref: ctaRef, isVisible: ctaVisible } = useIntersectionObserver();
  const { ref: relatedRef, isVisible: relatedVisible } = useIntersectionObserver();

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const articleTitle = (a: BlogArticle) => t(`blogArticles.${a.slug}.title`, { defaultValue: a.title });
  const articleExcerpt = (a: BlogArticle) => t(`blogArticles.${a.slug}.excerpt`, { defaultValue: a.excerpt });
  const articleReadTime = (a: BlogArticle) => t(`blogArticles.${a.slug}.readTime`, { defaultValue: a.readTime });

  // 404 fallback
  if (!article) {
    return (
      <section className="py-20">
        <div className="container text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('pageContent.blog.notFound.title')}</h1>
          <p className="text-xl text-gray-600 mb-8">
            {t('pageContent.blog.notFound.description')}
          </p>
          <Link
            href="/blog"
            className={buttonVariants({ variant: 'default', size: 'lg', className: 'hover-scale' })}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('pageContent.blog.backToBlog')}
          </Link>
        </div>
      </section>
    );
  }

  // Resolve translated content; fall back to article.content if translation missing
  const translatedContentRaw = t(`blogArticles.${article.slug}.content`, { returnObjects: true });
  const articleContent: BlogSection[] = Array.isArray(translatedContentRaw)
    ? (translatedContentRaw as BlogSection[])
    : article.content;

  return (
    <>
      {/* Article Header */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container">
          <div ref={headerRef} className={`max-w-3xl mx-auto fade-in-section ${headerVisible ? 'is-visible' : ''}`}>
            {/* Back Link */}
            <Link
              href="/blog"
              className="inline-flex items-center text-primary-500 font-medium hover:text-primary-600 transition-colors mb-8"
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('pageContent.blog.backToBlog')}
            </Link>

            {/* Category Badge */}
            <div className="mb-4">
              <span className="inline-flex items-center gap-1.5 bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold">
                <Tag className="h-3.5 w-3.5" />
                {article.categoryKey
                  ? t(`pageContent.blog.categories.${article.categoryKey}`, { defaultValue: article.category })
                  : article.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 break-words">
              {articleTitle(article)}
            </h1>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {t('pageContent.blog.publishedOn')} {formatDate(article.date)}
              </span>
              {article.updated && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  {t('pageContent.blog.updatedOn')} {formatDate(article.updated)}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <User className="h-4 w-4" />
                {t('pageContent.blog.byAuthor')} {article.author}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                {articleReadTime(article)}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div ref={bodyRef} className={`max-w-3xl mx-auto fade-in-section ${bodyVisible ? 'is-visible' : ''}`}>
            <div className="space-y-8">
              {articleContent.map((section, index) => (
                <div key={index}>
                  {section.heading && (
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 mt-8 break-words">
                      {section.heading}
                    </h2>
                  )}
                  {section.paragraphs?.map((paragraph, pIndex) => (
                    <p
                      key={pIndex}
                      className="text-gray-700 leading-relaxed text-lg mb-4"
                    >
                      {renderParagraph(paragraph, `${index}-${pIndex}`)}
                    </p>
                  ))}
                </div>
              ))}
            </div>

            {/* Back to blog link at bottom */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <Link
                href="/blog"
                className="inline-flex items-center text-primary-500 font-medium hover:text-primary-600 transition-colors"
              >
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                {t('pageContent.blog.backToBlog')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="container text-center">
          <div ref={ctaRef} className={`fade-in-section ${ctaVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('pageContent.blog.cta.title')}
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              {t('pageContent.blog.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/#upload-section"
                className={cn(buttonVariants({ variant: 'secondary', size: 'lg', className: 'px-8 hover-scale' }))}
              >
                {t('pageContent.blog.cta.uploadPitch')} <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                href="/pricing"
                className={cn(buttonVariants({ variant: 'outline', size: 'lg', className: 'px-8 hover-scale border-white text-white hover:bg-white/10' }))}
              >
                {t('pageContent.blog.cta.viewPricing')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16 md:py-20 bg-gray-50">
          <div className="container">
            <div ref={relatedRef} className={`fade-in-section ${relatedVisible ? 'is-visible' : ''}`}>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
                {t('pageContent.blog.relatedArticles')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                {relatedArticles.map((related: BlogArticle, index: number) => (
                  <Link
                    key={related.slug}
                    href={`/blog/${related.slug}`}
                    className={`group bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow hover-lift fade-in-section ${relatedVisible ? 'is-visible' : ''}`}
                    style={{ transitionDelay: `${index * 0.1}s` }}
                  >
                    <div className="p-6">
                      {/* Category Badge */}
                      <div className="mb-4">
                        <span className="inline-flex items-center gap-1 bg-primary-50 text-primary-600 px-3 py-1 rounded-full text-xs font-semibold">
                          <Tag className="h-3 w-3" />
                          {related.categoryKey
                            ? t(`pageContent.blog.categories.${related.categoryKey}`, { defaultValue: related.category })
                            : related.category}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {articleTitle(related)}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-2">
                        {articleExcerpt(related)}
                      </p>

                      {/* Meta */}
                      <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(related.date)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {articleReadTime(related)}
                        </span>
                      </div>

                      {/* Read More */}
                      <span className="inline-flex items-center text-primary-500 font-medium text-sm group-hover:text-primary-600 transition-colors">
                        {t('pageContent.blog.readMore')}
                        <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
